"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { UB_OPTANON_ALERT_BOX_CLOSED_COOKIE_NAME } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import Cookies from "js-cookie"
import { useTranslations } from "next-intl"

import { CustomerTypeCookieValue, UB_CUSTOMER_TYPE_COOKIE_NAME } from "@/lib/customer-type-cookies"
import { Button } from "@/components/ui/components"
import { setCustomerTypeCookie } from "@/app/actions"

/**
 * Aktualisiert den 'b2c'-Parameter in einer URL oder fügt ihn hinzu.
 * Das Hinzufügen kann über den allowInsert-Parameter gesteuert werden.
 *
 * @param {string} urlString Die ursprüngliche URL (z.B. von window.location.href).
 * @param {string|number} newB2CValue Der neue Wert für den 'b2c'-Parameter.
 * @param {boolean} [allowInsert=true] Steuert, ob der Parameter hinzugefügt
 *   werden darf, falls er nicht existiert.
 *   - true: Fügt den Parameter hinzu, wenn er fehlt.
 *   - false: Gibt die URL unverändert zurück, wenn der Parameter fehlt.
 * @returns {string} Die neue URL.
 */
function updateB2CParam(urlString: string, newB2CValue: string, allowInsert = true) {
  const url = new URL(urlString)
  const b2cExists = url.searchParams.has("b2c")

  // Fall 1: Der Parameter 'b2c' existiert bereits.
  // In diesem Fall wird er immer aktualisiert, egal was allowInsert sagt.
  if (b2cExists) {
    url.searchParams.set("b2c", newB2CValue)
    return url.toString()
  }

  // Fall 2: Der Parameter 'b2c' existiert nicht.
  // Jetzt prüfen wir, ob wir ihn einfügen dürfen.
  if (allowInsert) {
    url.searchParams.set("b2c", newB2CValue)
    return url.toString()
  }

  // Fall 3: Der Parameter existiert nicht und darf nicht eingefügt werden.
  // Gib die originale, unveränderte URL zurück.
  return urlString
}

export default function InitialCustomerTypeDialogClient() {
  const [visible, setVisible] = useState<boolean>(false)
  const { setIsB2BCustomer } = useUbStorefrontState()

  const intervalId = useRef<undefined | ReturnType<typeof setInterval>>(undefined)
  const t = useTranslations("storefront")

  const waitForOptanonAlertBoxClosed = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
    }
    intervalId.current = setInterval(() => {
      if (Cookies.get(UB_OPTANON_ALERT_BOX_CLOSED_COOKIE_NAME)) {
        const b2bCustomerCookieValue = Cookies.get(UB_CUSTOMER_TYPE_COOKIE_NAME)
        console.log("Did not found ubCustomerType cookie -> Ask for customer type" + b2bCustomerCookieValue)
        setVisible(!b2bCustomerCookieValue) // no dialog if cookie is set
        clearInterval(intervalId.current)
      }
    }, 500)
  }, [])

  function selectCustomerType(type: CustomerTypeCookieValue) {
    setCustomerTypeCookie(type)
      .then(() => setIsB2BCustomer(CustomerTypeCookieValue.isB2bCustomer(type)))
      .then(() => {
        const newHref = updateB2CParam(
          window.location.href,
          CustomerTypeCookieValue.isB2bCustomer(type) ? "0" : "1",
          false
        )
        window.location.replace(newHref)
      })
  }

  function selectB2B() {
    selectCustomerType(CustomerTypeCookieValue.B2B)
  }

  function selectB2C() {
    selectCustomerType(CustomerTypeCookieValue.B2C)
  }

  useEffect(() => {
    const cookieValue = Cookies.get(UB_CUSTOMER_TYPE_COOKIE_NAME)
    if (cookieValue !== CustomerTypeCookieValue.B2B && cookieValue !== CustomerTypeCookieValue.B2C) {
      waitForOptanonAlertBoxClosed()
    }
  }, [waitForOptanonAlertBoxClosed])

  return (
    <>
      {visible && (
        <div className={"fixed inset-0 z-[180] bg-black/90"}>
          <div className={"absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 pt-4"}>
            <div className={"flex flex-col gap-10 bg-white p-6"}>
              <h2 className={"text-2xl font-bold"}>{t("customer.initial.header")}</h2>
              <p className={"text-xl"}>{t("customer.initial.description")}</p>
              <div className={"flex  justify-between gap-14"}>
                <Button onClick={selectB2B} className={"grow text-xl font-bold"}>
                  {t("customer.b2b")}
                </Button>
                <Button onClick={selectB2C} className={"grow text-xl font-bold"}>
                  {t("customer.b2c")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
